/**
 * Formats `bytes` as a locale-aware human-readable size (e.g. "1.23 MB").
 * Uses SI (1000-based) units and a fixed 2-decimal precision via
 * `Intl.NumberFormat` so the decimal separator matches the user's locale.
 */
export function formatFileSize(bytes: number, locale: string): string {
  if (bytes < 1_000) return `${bytes} B`;
  let value: number;
  let unit: string;
  if (bytes < 1_000_000) {
    value = bytes / 1_000;
    unit = "KB";
  } else if (bytes < 1_000_000_000) {
    value = bytes / 1_000_000;
    unit = "MB";
  } else if (bytes < 1_000_000_000_000) {
    value = bytes / 1_000_000_000;
    unit = "GB";
  } else {
    value = bytes / 1_000_000_000_000;
    unit = "TB";
  }
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)} ${unit}`;
}

/**
 * Returns the final path segment of a POSIX-style path, or the whole input
 * if no separator is present.
 */
export function basenameFromPath(path?: string): string {
  if (!path) return "";
  const trimmed = path.replace(/\/+$/, "");
  const idx = trimmed.lastIndexOf("/");
  return idx >= 0 ? trimmed.slice(idx + 1) : trimmed;
}

/** Splits a POSIX-style path into its non-empty segments. */
export function pathSegments(path?: string): string[] {
  return (path ?? "").split("/").filter(Boolean);
}

/** The parent-folder segments of a path (all but the final segment). */
export function parentSegments(path?: string): string[] {
  return pathSegments(path).slice(0, -1);
}

/**
 * Whether a file's preview should be served inline — the browser plays/embeds it
 * directly (media, PDF, text) — rather than as a server-rendered preview image
 * (the drive preview endpoint only renders images). Shared by the space and
 * share-link viewers so their preview URLs can't drift apart.
 */
export function isInlinePreviewMime(mimeType?: string): boolean {
  const mime = mimeType ?? "";
  return (
    mime.startsWith("video") ||
    mime.startsWith("audio") ||
    mime.startsWith("text") ||
    mime.endsWith("pdf")
  );
}
